import { Tab, TabProps, Tabs, Utils } from "@blueprintjs/core";
import React from "react";

import { HexTab, HexTabProps } from "./HexTab.js";
import { HexTabLink, HexTabLinkProps } from "./HexTabLink.js";
import { InternalHexTab } from "./InternalHexTab.js";
import { InternalHexTabLink } from "./InternalHexTabLink.js";

/*
A custom override of the Blueprint `<Tabs>` component that enables us to
own the render process of `<Tab>` components.

We can override the internals even though they're private and TypeScript
complains. At runtime all methods are writable properties, and subclass field
initializers run after super(), so they are reliable.

See [source file](https://github.com/palantir/blueprint/blob/61f488f59ce56039d396bd2e8d8df46bc206327e/packages/core/src/components/tabs/tabs.tsx)
*/

/**
 * @ignore
 * @private
 */
// @ts-expect-error (error about separate declaration of private field) intentional, see above
export class InternalHexTabs extends Tabs {
  private override renderTabTitle = (
    child: React.ReactNode,
  ): React.ReactNode => {
    const parentId = this.props.id;
    // @ts-expect-error handleTabClick is private
    const onClick = this.handleTabClick;
    if (isTabElement(child)) {
      const selected = child.props.id === this.state.selectedTabId;
      const additionalProps = { parentId, selected, onClick };
      const icon =
        "icon" in child.props && typeof child.props.icon !== "string"
          ? child.props.icon
          : undefined;
      return (
        <InternalHexTab {...child.props} icon={icon} {...additionalProps} />
      );
    } else if (isTabLinkElement(child)) {
      const selected = child.props.id === this.state.selectedTabId;
      const additionalProps = { parentId, selected, onClick };
      return <InternalHexTabLink {...child.props} {...additionalProps} />;
    } else {
      return child;
    }
  };
}

type TabElement =
  | React.ReactElement<TabProps>
  | React.ReactElement<HexTabProps>;

function isTabElement(child: unknown): child is TabElement {
  return (
    Utils.isElementOfType(child, Tab) || Utils.isElementOfType(child, HexTab)
  );
}

type TabLinkElement = React.ReactElement<HexTabLinkProps>;

function isTabLinkElement(child: unknown): child is TabLinkElement {
  return Utils.isElementOfType(child, HexTabLink);
}
